/**
 * Universal PDP — Add-to-Cart
 *
 * Responsibility: resolve the correct variant ID + selling plan from the
 * custom quantity-card / flavor-selector UI, then hand off to NNHCart.
 *
 * Cart API calls, button state management, cart-drawer open, error toasts,
 * and event dispatching are all handled by nnh-atc.js (window.NNHCart).
 */

document.addEventListener('DOMContentLoaded', function () {
  initializeAddToCart();
  initializeSkioFlavorSync();
});

// ── Skio flavor sync ────────────────────────────────────────────────────────
// Updates skio-plan-picker.selectedVariant when the flavor selector changes.
function initializeSkioFlavorSync() {
  document.addEventListener('flavorChanged', function (event) {
    var skioComponent = document.querySelector('skio-plan-picker');
    if (!skioComponent || !skioComponent.product) return;

    var flavor = event.detail && event.detail.flavor;
    if (!flavor) return;

    var activeQtyOption = document.querySelector('.pdp-details__selection-option--active[data-quantity]');
    var quantity = activeQtyOption ? activeQtyOption.dataset.quantity : '3';

    var correctVariantId = findVariantByQuantityAndFlavor(quantity, flavor);
    if (!correctVariantId) return;

    var correctVariant = skioComponent.product.variants.find(function (v) {
      return String(v.id) === String(correctVariantId);
    });

    if (correctVariant && skioComponent.selectedVariant &&
        skioComponent.selectedVariant.id !== correctVariant.id) {
      skioComponent.selectedVariant = correctVariant;
      if (typeof skioComponent.requestUpdate === 'function') skioComponent.requestUpdate();
    }

    if (correctVariantId && window.triggerBoosterVariantSelect) {
      window.triggerBoosterVariantSelect(correctVariantId);
    }
  });
}

// ── Add-to-cart click handler ───────────────────────────────────────────────
function initializeAddToCart() {
  var addToCartBtns = document.querySelectorAll('.add-to-cart-btn');
  if (!addToCartBtns.length) return;

  addToCartBtns.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();

      if (this.dataset.outOfStock === 'true') return;

      var skioComponent = document.querySelector('skio-plan-picker');
      var subscriptionContainer = document.querySelector('.pdp-details__subscription-container');
      var oneTimeContainer = document.querySelector('#one-time-option');

      var isSubscriptionActive = subscriptionContainer &&
        subscriptionContainer.classList.contains('pdp-details__subscription-container--active');
      var isOneTimeActive = oneTimeContainer &&
        oneTimeContainer.classList.contains('pdp-details__one-time-container--active');

      // ── Path A: non-Skio product (simple variant list) ──────────────────
      if (!skioComponent) {
        var activeVariantOption = document.querySelector(
          '.pdp-details__selection-option--active[data-variant-id][data-type="quantity"]'
        );

        if (!activeVariantOption) {
          window.NNHCart && window.NNHCart.showToast(
            'Please select a variant before adding to cart.', 'error'
          );
          return;
        }

        if (activeVariantOption.dataset.available === 'false') {
          window.NNHCart && window.NNHCart.showToast(
            'This product is out of stock. Please select a different option.', 'error'
          );
          return;
        }

        var variantId = activeVariantOption.dataset.variantId;
        if (!variantId) {
          window.NNHCart && window.NNHCart.showToast(
            'Unable to determine product variant. Please try again.', 'error'
          );
          return;
        }

        window.NNHCart && window.NNHCart.addItem({ id: variantId, quantity: 1, button: this });
        return;
      }

      // ── Shared: quantity-card check ──────────────────────────────────────
      var activeQuantityOption = document.querySelector(
        '.pdp-details__selection-option--active[data-quantity]'
      );

      if (!activeQuantityOption) {
        window.NNHCart && window.NNHCart.showToast(
          'Please select a quantity before adding to cart.', 'error'
        );
        return;
      }

      if (activeQuantityOption.dataset.available === 'false') {
        window.NNHCart && window.NNHCart.showToast(
          'This option is out of stock. Please choose another.', 'error'
        );
        return;
      }

      var activeQty = activeQuantityOption.dataset.quantity;
      var selectedFlavor = window.getSelectedFlavor ? window.getSelectedFlavor() : null;
      var compareAtCents = getCompareAtCentsForQuantity(activeQty);
      var flavorProps = selectedFlavor ? { Flavor: selectedFlavor } : {};

      // ── Path B: Skio subscription ────────────────────────────────────────
      if (skioComponent && typeof skioComponent.addToCart === 'function' &&
          isSubscriptionActive && !isOneTimeActive) {

        // Sync Skio to the correct flavor + quantity variant before delegating
        if (selectedFlavor && skioComponent.product) {
          var correctVariantId = findVariantByQuantityAndFlavor(activeQty, selectedFlavor);

          if (!correctVariantId) {
            window.NNHCart && window.NNHCart.showToast(
              'Could not find the selected flavor variant. Please try again.', 'error'
            );
            return;
          }

          var correctVariant = skioComponent.product.variants.find(function (v) {
            return String(v.id) === String(correctVariantId);
          });

          if (correctVariant) {
            skioComponent.selectedVariant = correctVariant;
            if (typeof skioComponent.requestUpdate === 'function') skioComponent.requestUpdate();
          }
        }

        var skioVariantId = skioComponent.selectedVariant && skioComponent.selectedVariant.id;

        window.NNHCart && window.NNHCart.addViaSkio(skioComponent, {
          id: skioVariantId,
          quantity: 1,
          compare_at_cents: compareAtCents,
          properties: flavorProps,
          button: this
        });
        return;
      }

      // ── Path C: one-time / non-Skio fallback ────────────────────────────
      var variantData = getVariantData(activeQty, isSubscriptionActive);

      if (!variantData.variantId) {
        window.NNHCart && window.NNHCart.showToast(
          'Unable to determine product variant. Please try again.', 'error'
        );
        return;
      }

      var properties = Object.assign({}, flavorProps);
      if (compareAtCents) properties._compare_at_cents = String(compareAtCents);

      window.NNHCart && window.NNHCart.addItem({
        id: variantData.variantId,
        selling_plan: (variantData.sellingPlanId && isSubscriptionActive)
          ? variantData.sellingPlanId : '',
        quantity: 1,
        properties: properties,
        button: this
      });
    });
  });
}

// ── Variant resolution helpers ──────────────────────────────────────────────

/**
 * Resolve variant ID + selling plan ID (Enhanced with Dynamic Plan Data)
 * For Skio products: uses dynamic plan discovery, fallback to data attributes
 * For flavor products: delegates to findVariantByQuantityAndFlavor().
 */
function getVariantData(quantity, isSubscription) {
  var variantId = '';
  var sellingPlanId = '';

  var selectedFlavor = window.getSelectedFlavor ? window.getSelectedFlavor() : null;

  // Try dynamic plan data first if available
  if (window.SkioPlansCache && window.SkioPlansCache.initialized && isSubscription) {
    var plan = window.getDynamicPlanForQuantity ? window.getDynamicPlanForQuantity(quantity) : null;
    if (plan) {
      if (selectedFlavor) {
        variantId = findVariantByQuantityAndFlavor(quantity, selectedFlavor);
      } else {
        variantId = plan.variant ? String(plan.variant.id) : '';
      }
      sellingPlanId = String(plan.id);
      
      if (variantId && sellingPlanId) {
        return { variantId: variantId, sellingPlanId: sellingPlanId };
      }
    }
  }

  // Fallback to static data attributes
  var el = document.getElementById('pdp-variant-data');
  if (!el) return { variantId: '', sellingPlanId: '' };

  if (selectedFlavor) {
    variantId = findVariantByQuantityAndFlavor(quantity, selectedFlavor);
    if (!variantId) return { variantId: '', sellingPlanId: '' };
  } else {
    if (quantity === '1') {
      variantId = el.dataset.oneBottleVariantId || '';
      if (isSubscription) sellingPlanId = el.dataset.monthlyPlanId || '';
    } else if (quantity === '3') {
      variantId = el.dataset.threeBottleVariantId || '';
      if (isSubscription) sellingPlanId = el.dataset.quarterlyPlanId || '';
    } else if (quantity === '6') {
      variantId = el.dataset.sixBottleVariantId || '';
      if (isSubscription) sellingPlanId = el.dataset.semiAnnualPlanId || '';
    }
  }

  // Selling plan (second pass for flavor products where plan wasn't set above)
  if (isSubscription && !sellingPlanId) {
    if (quantity === '1') sellingPlanId = el.dataset.monthlyPlanId || '';
    else if (quantity === '3') sellingPlanId = el.dataset.quarterlyPlanId || '';
    else if (quantity === '6') sellingPlanId = el.dataset.semiAnnualPlanId || '';
  }

  // Fallback to 3-bottle if still no variant (shouldn't happen in normal use)
  if (!variantId) {
    variantId = el.dataset.threeBottleVariantId || '';
    if (isSubscription) sellingPlanId = el.dataset.quarterlyPlanId || '';
  }

  return { variantId: variantId, sellingPlanId: sellingPlanId };
}

/**
 * Return compare-at price in cents (Enhanced with Dynamic Plan Data)
 */
function getCompareAtCentsForQuantity(quantity) {
  // Try dynamic plan data first
  if (window.SkioPlansCache && window.SkioPlansCache.initialized) {
    var plan = window.getDynamicPlanForQuantity ? window.getDynamicPlanForQuantity(quantity) : null;
    if (plan && plan.compareAtPrice) {
      return parseInt(plan.compareAtPrice) || 0;
    }
  }

  // Fallback to static data
  var el = document.getElementById('variantPriceData');
  if (!el) return 0;
  if (quantity === '1') return parseInt(el.dataset.oneCompare) || 0;
  if (quantity === '3') return parseInt(el.dataset.threeCompare) || 0;
  if (quantity === '6') return parseInt(el.dataset.sixCompare) || 0;
  return 0;
}

/**
 * Find a variant whose title / options match both the given quantity and flavor.
 * Used for FloFit-style products where option1 = flavor and option2 = quantity (or vice versa).
 *
 * @param {string} quantity — '1', '3', or '6'
 * @param {string} flavor   — display flavor name, e.g. 'Double Chocolate'
 * @returns {string} variant ID, or '' if not found
 */
function findVariantByQuantityAndFlavor(quantity, flavor) {
  var variantsEl = document.getElementById('pdp-all-variants-data');
  if (!variantsEl) return '';

  var data;
  try { data = JSON.parse(variantsEl.textContent); } catch (e) { return ''; }

  var flavorLower = flavor.toLowerCase();
  var qStr = String(quantity);

  // Strategy 1: exact match in title (both flavor and quantity present)
  var match = data.variants.find(function (v) {
    if (!v.available) return false;
    var t = v.title.toLowerCase();
    var hasQty = t.includes(qStr + ' tub') || t.includes(qStr + '-pack') ||
                 t.includes(qStr + ' month') || t === qStr;
    return hasQty && t.includes(flavorLower);
  });
  if (match) return String(match.id);

  // Strategy 2: separate option1/option2 match
  match = data.variants.find(function (v) {
    if (!v.available) return false;
    var o1 = v.option1 ? String(v.option1).toLowerCase() : '';
    var o2 = v.option2 ? String(v.option2).toLowerCase() : '';

    function hasQty(s) {
      return s === qStr || s.startsWith(qStr + ' ') || s.includes(' ' + qStr + ' ') ||
             s.endsWith(' ' + qStr);
    }

    return (o1.includes(flavorLower) && hasQty(o2)) ||
           (o2.includes(flavorLower) && hasQty(o1));
  });
  if (match) return String(match.id);

  // Strategy 3: flexible — partial flavor word match + quantity
  var flavorWords = flavorLower.split(/\s+/).filter(function (w) { return w.length > 2; });
  match = data.variants.find(function (v) {
    if (!v.available) return false;
    var o1 = v.option1 ? String(v.option1).toLowerCase() : '';
    var o2 = v.option2 ? String(v.option2).toLowerCase() : '';
    var t  = v.title.toLowerCase();

    function hasQty(s) {
      return s === qStr || s.startsWith(qStr + ' ') || s.includes(' ' + qStr + ' ') ||
             s.endsWith(' ' + qStr) || t.includes(qStr + ' tub') || t.includes(qStr + '-pack');
    }

    var qInO1 = hasQty(o1), qInO2 = hasQty(o2);
    var hasQuantity = qInO1 || qInO2 || hasQty(t);
    if (!hasQuantity) return false;

    return flavorWords.some(function (w) {
      return t.includes(w) || o1.includes(w) || o2.includes(w);
    });
  });

  return match ? String(match.id) : '';
}

window.findVariantByQuantityAndFlavor = findVariantByQuantityAndFlavor;
